import React, { useContext, useEffect, useState, useRef } from "react";
import Button from "../../../components/Button";
import { StockSearchModal } from "./stockSelectorModal";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa";
import { IoIosAddCircleOutline } from "react-icons/io";
import { DispatchContext } from "../../../store/context";
import { UPDATE_BASE_CURRENCY } from "../../../store/types";
import { marketCurrencyMap, STOCK_EXCHANGES } from "../../../helper";

type StockSelectorType = {
  stocks: any[];
  onHandleGenerate: Function;
  epsGrowth: string;
  peEstimatea: String;
  primaryStock: any;
  setPrimaryStock: any;
  secondaryStocks: any;
  setSecondaryStocks: any;
  setScrollDirection: (value: "left" | "right") => void;
  loading: boolean;
  scrollUpdate: boolean;
  setScrollUpdate: any;
  mobileScrollIndex: number;
  setMobileScrollIndex: (value: number) => void;
  isMobile: boolean;
};
const StockCard = ({
  stock,
  isPrimary,
  onAddClick,
  onRemove,
  primaryStock,
  showAdd = true,
  disabled = false,
}: {
  stock: any;
  isPrimary: boolean;
  onAddClick?: () => void;
  onRemove?: (stock: any) => void;
  primaryStock?: any;
  showAdd?: boolean;
  disabled?: boolean;
}) => {
  if (!stock && showAdd) {
    return (
      <div
        className={`w-[220px] md:w-[180px] min-w-[220px] md:min-w-[180px] h-[66px] ${
          isPrimary ? "bg-[#EAECF0]" : "bg-white "
        } rounded-lg border border-gray-200 flex items-center justify-center cursor-pointer hover:border-blue-500 px-2 md:!mx-2 ${
          !isPrimary && !primaryStock ? "opacity-50 cursor-not-allowed" : ""
        } `}
        onClick={() => {
          if (isPrimary || (!isPrimary && primaryStock)) {
            onAddClick?.();
          }
        }}
      >
        <div
          className={`flex items-center text-[#0075E3] font-normal gap-x-2 !font-inter ${
            !isPrimary && !primaryStock
              ? "!cursor-not-allowed"
              : " cursor-pointer"
          }`}
        >
          <IoIosAddCircleOutline className="h-6 w-6 flex-shrink-0" />
          <span className="text-[14px] text-nowrap font-[500]">
            {isPrimary ? "Add Primary Stock" : "Add Stock"}
          </span>
        </div>
      </div>
    );
  }
  return (
    <>
      {showAdd && (
        <>
          <div
            className={`w-[220px] md:w-[180px] min-w-[220px] md:min-w-[180px] h-[66px] bg-white rounded-lg border border-gray-200 py-2 px-3 md:!mx-2 overflow-hidden`}
          >
            <div className="flex items-center w-full">
              <div className="rounded-full flex items-center justify-between gap-x-2 w-full overflow-hidden">
                <img
                  src={`/${stock.company_logo}`}
                  alt={stock.company_logo}
                  className="w-4 h-4 object-contain flex-shrink-0"
                />
                <div className="flex items-center gap-x-[6px] min-w-0 flex-1">
                  <h1 className="font-semibold text-sm truncate min-w-0 flex-1">
                    {stock?.company_name}
                  </h1>
                  <button
                    className="w-4 h-4 flex-shrink-0 rounded-full flex items-center disabled:!cursor-not-allowed justify-center border border-gray-50 cursor-pointer hover:bg-gray-100"
                    onClick={() => onRemove?.(stock)}
                    disabled={disabled}
                  >
                    x
                  </button>
                </div>
              </div>
            </div>
            <div className="border mt-2 border-[#E6E6E6] w-full" />
            <div className="flex items-center justify-between w-full">
              <div className="min-w-0 flex-shrink">
                <h2 className="text-[12px] !font-[400] text-[#6C737F] truncate">
                  {stock.ticker_symbol}
                </h2>
              </div>
              <div className="flex items-center justify-end flex-shrink-0 ml-2">
                <h2 className="text-[12px] font-[400] text-nowrap">
                  {marketCurrencyMap[stock?.short_name] ?? "USD"}
                  {stock?.closed}
                </h2>
                <div
                  className={`text-[12px] px-1 font-[400] ${
                    stock?.change_perc >= 0 ? "text-green-600" : "text-red-600"
                  }`}
                >
                  ({stock?.change_perc > 0 ? "+" : ""}
                  {stock?.change_perc}%)
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export const StockSelector = (props: StockSelectorType) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isAddingPrimary, setIsAddingPrimary] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [shouldGenerate, setShouldGenerate] = useState(false);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [touchOffset, setTouchOffset] = useState(0);
  const [isTouching, setIsTouching] = useState(false);
  const carouselRef = useRef<HTMLDivElement>(null);

  const stocksFromExchanges = STOCK_EXCHANGES?.flatMap(
    (exchange) =>
      (props.stocks &&
        props.stocks?.find((section) => section?.section === exchange)
          ?.options) ||
      [],
  );

  const activeStocks = stocksFromExchanges?.filter(
    (stock: any) => stock.status === 1,
  );

  const filteredStocks = activeStocks?.filter((stock: any) => {
    const isSelected =
      props.primaryStock?.id === stock.id ||
      props.secondaryStocks.some(
        (secondaryStock: any) => secondaryStock.id === stock.id,
      );

    return (
      !isSelected &&
      (stock.ticker_symbol
        ?.toLowerCase()
        ?.includes(searchQuery?.toLowerCase()) ||
        stock.company_name
          ?.toLowerCase()
          ?.includes(
            searchQuery?.toLowerCase() ||
              stock.short_name
                ?.toLowerCase()
                ?.includes(searchQuery?.toLowerCase()),
          ))
    );
  });

  const dispatch = useContext(DispatchContext);

  const handleSelectStock = (stock: any) => {
    if (isAddingPrimary) {
      props.setPrimaryStock(stock);
      const currency = marketCurrencyMap[stock?.short_name] ?? "USD";
      dispatch({ type: UPDATE_BASE_CURRENCY, payload: currency });
    } else {
      if (props.secondaryStocks.length < 4) {
        props.setSecondaryStocks((prevStocks: any[]) => [...prevStocks, stock]);
      }
    }
    setIsModalOpen(false);
  };

  const handleRemoveStock = (stockToRemove: any) => {
    if (props.primaryStock && props.primaryStock?.id === stockToRemove.id) {
      props.setPrimaryStock(null);
      props.setSecondaryStocks([]);
    } else {
      props.setSecondaryStocks((prevStocks: any[]) =>
        prevStocks.filter((stock: any) => stock.id !== stockToRemove.id),
      );
    }
    setShouldGenerate(true);
  };

  const openStockModal = (isPrimary = false) => {
    if (!isPrimary && !props.primaryStock) {
      return;
    }
    setIsAddingPrimary(isPrimary);
    setIsModalOpen(true);
  };

  const navigateCarousel = (direction: "left" | "right") => {
    props.setScrollUpdate(!props.scrollUpdate);
    if (
      direction === "left" &&
      (props.isMobile ? props.mobileScrollIndex > 0 : currentIndex > 0)
    ) {
      if (props.isMobile) {
        props.setMobileScrollIndex(props.mobileScrollIndex - 1);
      } else {
        setCurrentIndex(currentIndex - 1);
      }
      props.setScrollDirection("left");
    } else if (direction === "right") {
      props.setScrollDirection("right");
      const visibleCards = props.isMobile ? 1 : 2; // Use 1 for mobile, 2 for desktop
      const totalCards = props.isMobile
        ? props.secondaryStocks.length + 1 // Primary + secondary cards on mobile
        : props.secondaryStocks.length +
          (props.secondaryStocks.length < 4 ? 1 : 0); // Only secondary cards on desktop
      const maxIndex = Math.max(0, totalCards - visibleCards);

      if (props.isMobile) {
        if (props.mobileScrollIndex < maxIndex) {
          props.setMobileScrollIndex(props.mobileScrollIndex + 1);
        }
      } else {
        if (currentIndex < maxIndex) {
          setCurrentIndex(currentIndex + 1);
        }
      }
    }
  };

  // Touch event handlers for mobile devices
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
    setTouchOffset(0);
    setIsTouching(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchStart) return;

    const currentTouch = e.targetTouches[0].clientX;
    const offset = currentTouch - touchStart;
    setTouchOffset(offset);
    setTouchEnd(currentTouch);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      // Swipe left - navigate right
      const visibleCards = 1;
      const totalCards = props.secondaryStocks.length + 1; // Primary + secondary cards on mobile
      const maxIndex = Math.max(0, totalCards - visibleCards);

      if (props.mobileScrollIndex < maxIndex) {
        navigateCarousel("right");
      }
    } else if (isRightSwipe) {
      // Swipe right - navigate left
      if (props.mobileScrollIndex > 0) {
        navigateCarousel("left");
      }
    }

    // Reset touch values
    setTouchStart(null);
    setTouchEnd(null);
    setTouchOffset(0);
    setIsTouching(false);
  };

  useEffect(() => {
    if (shouldGenerate) {
      props.onHandleGenerate(
        props.primaryStock,
        props.secondaryStocks,
        props.peEstimatea,
        props.epsGrowth,
      );
      setShouldGenerate(false);
    }
  }, [
    shouldGenerate,
    props.primaryStock,
    props.secondaryStocks,
    props.peEstimatea,
    props.epsGrowth,
  ]);
  return (
    <div className="w-full" key={props.primaryStock}>
      <div className="flex md:flex-row flex-col lg:items-center w-full  ">
        <h2 className="text-[16px] font-bold w-full font-inter text-nowrap max-w-[160px]">
          Compare Stocks
        </h2>
        <div className="relative mt-4 md:mt-0 max-w-full w-full overflow-hidden">
          <Button
            variant="ghost"
            className=" hidden md:absolute left-4 top-1/2 -translate-y-1/2 z-20 bg-[#F9FAFB] w-[32px] h-[32px] rounded-full p-2 md:flex items-center justify-center"
            onClick={() => navigateCarousel("left")}
            disabled={
              props.isMobile
                ? props.mobileScrollIndex === 0
                : currentIndex === 0
            }
          >
            <FaArrowLeft className="h-6 w-6 text-[#0075E3]" />
          </Button>

          <div
            className="md:mx-8"
            ref={carouselRef}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div className="relative ">
              {/* Desktop Primary stock card - only visible above 640px */}
              <div className="hidden md:block absolute left-0 top-0 z-10">
                <StockCard
                  stock={props.primaryStock}
                  isPrimary={true}
                  onAddClick={() => openStockModal(true)}
                  onRemove={handleRemoveStock}
                  primaryStock={props.primaryStock}
                  disabled={props.secondaryStocks?.length}
                />
              </div>

              {/* Main container for all cards (mobile primary + secondary) */}
              <div
                className="flex items-center transition-transform duration-300 ease-in-out "
                style={{
                  transform: `translateX(calc(-${
                    (props.isMobile ? props.mobileScrollIndex : currentIndex) *
                    (props.isMobile ? 230 : 196)
                  }px + ${touchOffset}px))`,
                  gap: `${props.isMobile ? "12px" : "0px"}`,
                  transition: isTouching
                    ? "none"
                    : "transform 0.3s ease-in-out",
                }}
              >
                {/* Mobile Primary stock card - only visible below 640px */}
                <div
                  className="md:hidden z-10 w-[220px]"
                  style={{
                    width: "220px",
                  }}
                >
                  <StockCard
                    stock={props.primaryStock}
                    isPrimary={true}
                    onAddClick={() => openStockModal(true)}
                    onRemove={handleRemoveStock}
                    primaryStock={props.primaryStock}
                    disabled={props.secondaryStocks?.length}
                  />
                </div>

                {/* Desktop Placeholder for primary card */}
                <div className="hidden md:block min-w-[220px] md:min-w-[180px] mx-3 md:!mx-2 opacity-0" />

                {/* Secondary stocks */}
                {props.secondaryStocks.map((stock: any) => (
                  <StockCard
                    key={stock.id}
                    stock={stock}
                    isPrimary={false}
                    onRemove={handleRemoveStock}
                    primaryStock={props.primaryStock}
                  />
                ))}
                {props.secondaryStocks.length < 4 && (
                  <StockCard
                    stock={null}
                    isPrimary={false}
                    onAddClick={() => openStockModal(false)}
                    primaryStock={props.primaryStock}
                    showAdd={true}
                  />
                )}
              </div>
            </div>
          </div>

          <Button
            variant="ghost"
            className=" hidden md:absolute right-6 top-1/2 -translate-y-1/2 z-20 bg-[#F9FAFB] w-[32px] h-[32px] rounded-full p-2 md:flex items-center justify-center disabled:!cursor-not-allowed"
            onClick={() => navigateCarousel("right")}
            disabled={(() => {
              const visibleCards = props.isMobile ? 1 : 2;
              const totalCards = props.isMobile
                ? props.secondaryStocks.length + 1
                : props.secondaryStocks.length +
                  (props.secondaryStocks.length < 4 ? 1 : 0);
              const maxIndex = Math.max(0, totalCards - visibleCards);
              return (
                (props.isMobile
                  ? props.mobileScrollIndex >= maxIndex
                  : currentIndex >= maxIndex) ||
                props.secondaryStocks.length >= 5
              );
            })()}
          >
            <FaArrowRight className="h-6 w-6 text-[#0075E3]" />
          </Button>
        </div>
        <StockSearchModal
          isModalOpen={isModalOpen}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          filteredStocks={filteredStocks}
          handleSelectStock={handleSelectStock}
          setIsModalOpen={setIsModalOpen}
          callCompareFunction={() => setShouldGenerate(true)}
          loading={props.loading}
        />
      </div>
    </div>
  );
};
